import { useProductStore } from '@/stores/product';
import { CustomizationArea } from '@/types/Product';
import { Engine } from '@/engine/core/Engine';
import { waitForCanvas } from '@/utils/canvasUtils';

export function init(constructor: any, props: any = {}) {
    const obj = new constructor();
    Object.keys(props).forEach((key) => {
        obj[key] = props[key];
    });
    return obj;
}

export function range(start: number | string, end: number | string): number[] {
    return [...Array(+end + 1).keys()].slice(+start);
}

export function color() {
    const r = () => Math.floor(Math.random() * 256);
    return `rgb(${r()},${r()},${r()})`;
}

export function convertToNumber(value: string | number) {
    return typeof value === 'string' ? parseFloat(value) : value;
}

export function getSizeFromSVGString(svgString: string) {
    const parser = new DOMParser();
    const svg = parser.parseFromString(svgString, 'image/svg+xml');
    const svgElement = svg.querySelector('svg');
    const viewBox = svgElement?.getAttribute('viewBox')?.split(' ');
    if (!viewBox) {
        return;
    }
    const viewBoxWidth = viewBox[2];
    const viewBoxHeight = viewBox[3];
    return {
        width: parseInt(viewBoxWidth),
        height: parseInt(viewBoxHeight)
    };
}

export async function imageUrlToBase64(url: string): Promise<string> {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });
    } catch (error) {
        throw new Error(`Failed to convert image URL to base64: ${error}`);
    }
}

export async function getScale(base64: string): Promise<number> {
    try {
        // Attendre que le canvas soit prêt
        const canvasReady = await waitForCanvas();
        if (!canvasReady) {
            throw new Error('Canvas not ready for getScale');
        }

        const productStore = useProductStore();
        const canvas = Engine.getInstance().canvas;
        const customizationArea = productStore.product.uvMapDataList[0];

        const scale = Math.min(canvas.width / customizationArea.width, canvas.height / customizationArea.height);
        const scaledCustomizationArea = {
            width: customizationArea.width * scale,
            height: customizationArea.height * scale
        };

        return new Promise((resolve, reject) => {
            const newImage = document.createElement('img');
            newImage.onload = () => {
                // 🎯 FIX: Ne PAS limiter à defaultScale, calculer le ratio réel
                // Pour fitter l'image dans le gabarit, on divise gabarit / image
                const scaleToFitWidth = scaledCustomizationArea.width / newImage.width;
                const scaleToFitHeight = scaledCustomizationArea.height / newImage.height;
                const calculatedScale = Math.min(scaleToFitWidth, scaleToFitHeight);

                console.log('📐 getScale() calculated:', {
                    imageSize: `${newImage.width}x${newImage.height}`,
                    gabaritSize: `${scaledCustomizationArea.width}x${scaledCustomizationArea.height}`,
                    scaleToFitWidth,
                    scaleToFitHeight,
                    finalScale: calculatedScale
                });

                resolve(calculatedScale);
            };
            newImage.onerror = () => reject(new Error('Failed to load image'));
            newImage.src = base64;
        });
    } catch (error) {
        console.error('Error while getting scale:', error);
        return 1;
    }
}

export async function convertToBase64Png(base64: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0);

            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = base64;
    });
}

export function deepMergeJson(baseJson: any, newJson: any) {
    const mergedJson = { ...baseJson };

    for (const key in newJson) {
        if (newJson[key] instanceof Object && key in baseJson) {
            mergedJson[key] = deepMergeJson(baseJson[key], newJson[key]);
        } else {
            mergedJson[key] = newJson[key];
        }
    }

    return mergedJson;
}

export function getOffsetPercentages(customizationArea: { width: number; height: number }): {
    start: number;
    end: number;
} {
    const width = customizationArea.width;
    const height = customizationArea.height;

    const longSide = Math.max(width, height);
    const shortSide = Math.min(width, height);

    const offset = (longSide - shortSide) / 2;
    const start = (offset / longSide) * 100;
    const end = ((offset + shortSide) / longSide) * 100;

    return {
        start,
        end
    };
}

export function getScaledCustomizationArea(
    customizationArea: { width: number; height: number },
    canvas: HTMLCanvasElement
): CustomizationArea {
    const width = customizationArea.width;
    const height = customizationArea.height;
    const { start, end } = getOffsetPercentages(customizationArea);

    const scaledCustomizationArea = {} as CustomizationArea;
    if (width >= height) {
        scaledCustomizationArea.left = 0;
        scaledCustomizationArea.top = (start * canvas.height) / 100;
        scaledCustomizationArea.width = canvas.width;
        scaledCustomizationArea.height = ((end - start) * canvas.height) / 100;
    } else if (height > width) {
        scaledCustomizationArea.left = (start * canvas.height) / 100;
        scaledCustomizationArea.top = 0;
        scaledCustomizationArea.width = ((end - start) * canvas.width) / 100;
        scaledCustomizationArea.height = canvas.height;
    }
    scaledCustomizationArea.centerY = scaledCustomizationArea.top + scaledCustomizationArea.height / 2;
    scaledCustomizationArea.centerX = scaledCustomizationArea.left + scaledCustomizationArea.width / 2;

    return scaledCustomizationArea;
}

/**
 * Aperçus du produit courant, délégués au type de support.
 *
 * Signature et contrat inchangés (tableau de dataURL, `attempts` pour absorber
 * un rendu vide) : les appelants — validation panier, vignette de sauvegarde,
 * captures par zone du BAT — n'ont pas à savoir si le produit est 2D ou 3D. Le
 * corps 3D historique vit désormais dans `product-types/three-d/capture.ts`.
 *
 * ⚠️ IMPORT DYNAMIQUE OBLIGATOIRE, ne pas remonter en tête de fichier.
 * `main.ts` importe `App.vue` de façon STATIQUE : tout le graphe statique de
 * l'app est donc évalué AVANT `registerPlugins(app)`, qui installe Pinia. Or
 * ce module est atteint très tôt (App.vue -> useCustomFonts -> stores -> mappers
 * -> ici) et le registre mène à `Studio.ts`, qui instancie ses stores au niveau
 * module. Un import statique ici casse donc le démarrage avec
 * « getActivePinia() was called but there was no active Pinia ».
 * Le graphe est verrouillé par `scripts/check-import-graph.mjs`.
 */
export async function generateScreenshots(
    attempts: number,
    backgroundColor: string,
    options: { scope?: 'product' | 'activeSurface' } = {}
): Promise<string[]> {
    const { currentProductTypeAdapter } = await import('@/product-types/registry');
    return currentProductTypeAdapter().capturePreviews({
        attempts,
        backgroundColor,
        scope: options.scope ?? 'product'
    });
}

export function getFormattedDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}_${hours}H${minutes}`;
}

export function getPositionOnElement(
    element?: HTMLElement,
    position?: { x?: number; y?: number } | null
): number[] | undefined {
    if (!position || !position.x || !position.y || !element) {
        return;
    }
    const domRect = element.getBoundingClientRect();

    return [(position.x - domRect.left) / domRect.width, (position.y - domRect.top) / domRect.height];
}
